import { useMutation } from '@tanstack/react-query';

import { REPORTING_LE_LENS_READ_PATH } from '~/consts/reportingLeLensApi';

import type { LensCardType } from '~/types/leLens';

import { requestWithTimeout } from '~/utils/requestWithTimeout';
import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

/**
 * One card as the reading sees it: what it is called, what it draws, and the
 * rows it is drawing, after the reader's own filters and selections.
 */
export type ReadLensSectionCard = {
  title: string;
  type: LensCardType;
  columns: Array<string>;
  rows: Array<Record<string, unknown>>;
  /** Present when the rows sent are a slice, so the reading describes the slice. */
  truncatedNote?: string;
  comparisonLabel?: string;
  comparisonRows?: Array<Record<string, unknown>>;
};

export type ReadLensSectionInput = {
  question: string;
  period: string;
  cards: Array<ReadLensSectionCard>;
};

/** The server bounds these too; matching them here keeps an oversized request off the wire. */
export const READ_SECTION_MAX_CARDS = 6;
export const READ_SECTION_MAX_ROWS = 60;

/**
 * Ask for a written reading of one section.
 *
 * Deliberately a mutation rather than a query: a reading is work someone asked
 * for, not state to be kept warm. Caching it under a query key would spend a
 * model call on every mount, which is exactly what the saved `viewSignature`
 * exists to avoid.
 */
export function useReadLensSection() {
  return useMutation({
    mutationFn: async ({ question, period, cards }: ReadLensSectionInput) => {
      const response = await requestWithTimeout<{ reading?: string }>(
        resolveReportingLeLensUrl(REPORTING_LE_LENS_READ_PATH),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            question,
            period,
            cards: cards.slice(0, READ_SECTION_MAX_CARDS).map((card) => ({
              ...card,
              rows: card.rows.slice(0, READ_SECTION_MAX_ROWS),
              ...(card.comparisonRows ? { comparisonRows: card.comparisonRows.slice(0, READ_SECTION_MAX_ROWS) } : {}),
            })),
          }),
        },
        'This reading is taking too long. The cards below are unaffected.',
        30_000,
      );
      return response?.reading?.trim() ?? '';
    },
    retry: false,
  });
}
